/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { mapOptions } from '../../../config/MapConfig';
import { Sync, NotSync } from '../../../constants/Icon';
import { DataContext } from '../../../context/DataContext';
import MapContext from '../../../context/MapContext';
import useMapTracking from '../../../hooks/useMapTracking';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { config, SETTING_KEY } from '../../../config/Config';
import { handleMessage } from '../../../helper/utils';
import moment from 'moment';
import { createOrderOdm } from '../../../helper/order/createOrderOdm';

const zoom = 13;

const MapDom = ({
  id,
  children,
  onDblClick,
  onDragEnd,
  onDragStart,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onMouseUp,
  onRightClick,
  onClick,
  onDrag,
  onLoad,
  onUnmount,
  boundsChange,
  ...props
}) => {
  const ref = useRef();
  const { t } = useTranslation();

  const [drawingManager, setDrawingManager] = useState(null);
  const { isOpenFetchDataWhenZoom, onToggleSyncDataFilterMap, isLoadingFetchDataWhenZoom } =
    useContext(DataContext);

  const [customControls, setCustomControls] = useState([]);
  const [selectedShape, setSelectedShape] = useState(null);
  const [isExportExcel, setIsExportExcel] = useState(false);

  const [currentMarker, setCurrentMarker] = useState(null);
  // const [isCreatingOrderOdm, setIsCreatingOrderOdm] = useState(false);
  const dataMarker = useRef(null);
  // const dataCircle = useRef(null);

  const {
    center,
    polygonCompleted,
    onChange,
    fitBoundsCenter,
    driverId,
    isRenderNewMaps,
    orders,
    polygon,
    drivers,
    setBoundaries,
    onGetCurrentBoundary,
    getBounds,
    map,
    setMap,
  } = useMapTracking();
  const driverIds = drivers.map((driver) => driver?.driver_id);
  const activeFitBounds = driverId ? true : false;
  const haveOrder = orders?.length > 0;

  const event = useRef(null);

  const [valueSettingODM, setValueSettingODM] = useState({});
  const getSetting = async () => {
    const { data } = await axios.get(config.API_URL.SETTING.GET, {
      params: {
        key: SETTING_KEY.SEND_ORDER_FILTERING,
      },
      headers: config.headers,
    });
    const { setting } = data;
    setValueSettingODM(setting);
  };

  useEffect(() => {
    getSetting();
  }, []);

  //set selection to a shape
  function setSelection(shape) {
    setSelectedShape(shape);
    shape.setEditable(true);
  }

  //delete selected shape
  function deleteSelectedShape() {
    polygonCompleted();
    if (!selectedShape) {
      setMap(null);
      return;
    }
    selectedShape.setMap(null);
    setSelectedShape(null);
  }

  const exportExcel = () => {
    setIsExportExcel(true);
    try {
      axios
        .post(
          config.API_URL.LOCATION.EXPORT_EXCEL_DRIVERS,
          {
            driverIds: driverIds,
          },
          {
            headers: {
              ...config.headers,
              'Content-Disposition': 'attachment; filename=template.xlsx',
              'Content-Type': 'application/json',
            },
            responseType: 'arraybuffer',
          }
        )
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `Excel-driver-${moment().locale('en').format('MM-DD-YYYY')}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          setIsExportExcel(false);
        });
    } catch (errors) {
      handleMessage('error', 'Export excel failed! Please export excel again.');
      setIsExportExcel(false);
    }
  };

  useEffect(() => {
    if (drawingManager === null) return;
    if (polygon.length === 0) {
      drawingManager.setOptions({
        drawingControl: true,
      });
    }
    if (polygon.length !== 0) {
      drawingManager.setOptions({
        drawingControl: false,
      });
    }
  }, [polygon, drawingManager]);

  useEffect(() => {
    const customs = React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type.displayName === 'CustomControl') {
        return child;
      }
    });
    setCustomControls(customs);
  }, [children]);

  useEffect(() => {
    if (map && customControls.length) {
      map.controls[window.google.maps.ControlPosition.TOP_CENTER] = [];
      customControls.map((control) => {
        const container = window.document.createElement('div');
        ReactDOM.render(control, container);
        map.controls[window.google.maps.ControlPosition[control.props.position]].push(container);
        return null;
      });
    }
  }, [map, customControls]);

  useEffect(() => {
    if (ref.current && !map) {
      //
      const overlayComplete = 'overlaycomplete';
      const colorPolygon = '#be2448';
      const windowMaps = window.google.maps;
      const defaultPolygon = new windowMaps.Polygon({
        paths: polygon,
        editable: true,
        strokeColor: colorPolygon,
        fillColor: colorPolygon,
      });
      const windowDrawing = windowMaps.drawing;
      const curMap = new windowMaps.Map(ref.current, {
        ...mapOptions,
        ...props,
        center,
        zoom,
      });

      const drawingManager = new windowDrawing.DrawingManager({
        drawingMode: windowDrawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: windowMaps.ControlPosition.TOP_CENTER,
          drawingModes: [windowDrawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          editable: true,
          strokeColor: colorPolygon,
          fillColor: colorPolygon,
        },
        isDrawingModeEnabled: true,
      });
      if (polygon) {
        defaultPolygon.setMap(curMap);
      }

      setMap(curMap);

      setDrawingManager(drawingManager);
      drawingManager.setMap(curMap);
      drawingManager.setDrawingMode(null);
      windowMaps.event.addListener(drawingManager, overlayComplete, function (event) {
        // Switch to non-drawing mode after drawing a shape.
        drawingManager.setDrawingMode(null);
        var newShape = event.overlay;
        newShape.type = event.type;
        //after drawing = set selected
        setSelection(newShape);

        polygonCompleted(event);
      });
      if (polygonCompleted) {
        windowMaps.event.addListener(drawingManager, overlayComplete, function (event) {
          polygonCompleted(event);
        });
      }
    }
  }, [ref, map, props, polygonCompleted, customControls, selectedShape]);

  useEffect(() => {
    let listenerPolygonSet_at = null;
    let listenerPolygonInsert_att = null;
    const windowMaps = window.google.maps;

    if (selectedShape) {
      listenerPolygonSet_at = windowMaps.event.addListener(
        selectedShape.getPath(),
        'set_at',
        function () {
          const data = { overlay: selectedShape };
          polygonCompleted(data);
        }
      );
      listenerPolygonInsert_att = windowMaps.event.addListener(
        selectedShape.getPath(),
        'insert_at',
        function () {
          const data = { overlay: selectedShape };
          polygonCompleted(data);
        }
      );
    }
    return () => {
      if (listenerPolygonSet_at !== null) {
        listenerPolygonSet_at.remove();
      }
      if (listenerPolygonInsert_att !== null) {
        listenerPolygonInsert_att.remove();
      }
    };
  }, [selectedShape, drawingManager]);
  useEffect(() => {
    if (map) {
      const drawBtn = document.querySelector('[aria-label="Draw a shape"]');
      if (drawBtn) {
        drawBtn.addEventListener('click', () => {
          deleteSelectedShape();
        });
      }
    }
  }, [map, selectedShape]);

  const onChangeCallBack = useCallback(
    debounce((params) => onChange(params, isOpenFetchDataWhenZoom), 1000),
    [isOpenFetchDataWhenZoom]
  );

  useEffect(() => {
    let eventDrag = null;
    let eventZoomChange = null;
    if (map) {
      eventDrag = window.google.maps.event.addListener(map, 'dragend', () => {
        const bounds = getBounds(map);
        onChangeCallBack({ bounds });
      });
      eventZoomChange = window.google.maps.event.addListener(map, 'zoom_changed', () => {
        const bounds = getBounds(map);
        onChangeCallBack({ bounds });
        // if (event.current === null) {
        //   event.current = window.google.maps.event.addListener(map, 'zoom_changed', () => {
        //     console.log('zoom_changed event');
        //     const bounds = getBounds(map);
        //     onChangeCallBack({ bounds });
        //   });
        // }
      });

      // function placeMarker(location) {
      //   var marker = new window.google.maps.Marker({
      //     position: location,
      //     map: map,
      //   });

      //   if (dataMarker.current) {
      //     dataMarker.current.setMap(null);
      //   }
      //   dataMarker.current = marker;
      // }

      // window.google.maps.event.addListener(map, 'click', function (event) {
      //   placeMarker(event.latLng);
      //   setCurrentMarker({
      //     lat: event.latLng.lat(),
      //     lng: event.latLng.lng(),
      //   });

      //   var newShape = new window.google.maps.Circle({
      //     fillColor: '#ffff00',
      //     fillOpacity: 0.2,
      //     strokeWeight: 0,
      //     clickable: true,
      //     zIndex: 100,
      //     editable: false,
      //     center: { lat: event.latLng.lat(), lng: event.latLng.lng() },
      //     radius: (valueSettingODM?.max_distance ?? 5) * 1000,
      //     // km
      //     map,
      //   });
      //   if (dataCircle.current) {
      //     dataCircle.current.setMap(null);
      //   }
      //   dataCircle.current = newShape;
      // });
    }
    return () => {
      if (map) {
        if (eventDrag !== null) {
          eventDrag.remove();
        }
        if (eventZoomChange !== null) {
          eventZoomChange.remove();
        }
      }
      // eventZoomChange.remove();
    };
  }, [onChangeCallBack, map, valueSettingODM?.max_distance]);
  const removeEventZoomChange = () => {
    if (event?.current) {
      event?.current?.remove();
      event.current = null;
    }
  };

  useEffect(() => {
    if (window && window?.google?.maps && map && isRenderNewMaps) {
      const bounds = new window.google.maps.LatLngBounds();
      fitBoundsCenter?.map((item) => bounds.extend(item));
      if (!activeFitBounds) {
        removeEventZoomChange();
        // if (isAutoFitBounds) {
        //   map.fitBounds(bounds);
        // }
      } else {
        if (haveOrder) {
          removeEventZoomChange();
          // if (isAutoFitBounds) {
          //   map.fitBounds(bounds, paddingFitBounds);
          // }
        } else {
          // if (isAutoFitBounds) {
          //   map.fitBounds(bounds);
          //   map?.setZoom(zoomDoesHaveOrder);
          // }
        }
      }
    }
  }, [window, map, fitBoundsCenter, activeFitBounds, isRenderNewMaps, haveOrder]);

  // const handleCreateOrder = () => {
  //   createOrderOdm(currentMarker, setIsCreatingOrderOdm, () => {
  //     const newBoundary = cloneDeep(onGetCurrentBoundary());
  //     setBoundaries(newBoundary);
  //   });
  // };

  return (
    <>
      <MapContext.Provider value={map}>
        <div className="relative w-2/3">
          <div ref={ref} className="maps !w-full"></div>

          {polygon.length !== 0 && (
            <div className="absolute top-2 left-[45%]">
              <button
                disabled={isExportExcel}
                className={`refresh-button  ${
                  isExportExcel ? 'opacity-40 border-red-200' : 'opacity-100 '
                } `}
                onClick={exportExcel}
              >
                Download excel
              </button>
              <button
                type="button"
                onClick={deleteSelectedShape}
                className="bg-white px-2 py-[3px] rounded-md"
              >
                {t('delete_polygon')}
              </button>
            </div>
          )}
          <button
            disabled={isLoadingFetchDataWhenZoom}
            className={`absolute bottom-[200px] right-[10px]  w-[40px] h-[40px] rounded-[2px] flex justify-center items-center ${
              isOpenFetchDataWhenZoom ? 'bg-white' : 'bg-[#E1E1E1]'
            }`}
            onClick={onToggleSyncDataFilterMap}
          >
            <div className="w-[20px] h-[20px]">
              {isOpenFetchDataWhenZoom ? <Sync /> : <NotSync />}
            </div>
          </button>
          {/* {currentMarker && (
            <div className="bg-white  absolute top-0 left-[55%] z-[100] space-y-2 flex flex-col p-5 text-xl">
              <div className="flex flex-row gap-x-1 ">
                <p className="font-bold">lat: </p>
                <p className="font-weight">{currentMarker.lat}</p>
              </div>
              <div className="flex flex-row gap-x-1">
                <p className="font-bold">lng: </p> <p>{currentMarker.lng}</p>
              </div>
              <div className="flex flex-row gap-x-1">
                <p className="font-bold">Radius: </p> <p>{valueSettingODM?.max_distance}Km</p>
              </div>
              <div className="flex gap-3">
                <button
                  disabled={isCreatingOrderOdm}
                  onClick={handleCreateOrder}
                  className="p-4 bg-green-500 border text-white rounded-md disabled:opacity-50"
                >
                  Create order Odm
                </button>
                <button
                  className="p-4 text-white border rounded-md bg-red-500"
                  onClick={() => {
                    dataMarker.current.setMap(null);
                    setCurrentMarker(null);
                    dataCircle.current.setMap(null);
                  }}
                >
                  delete
                </button>
              </div>
            </div>
          )} */}
        </div>

        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type.displayName !== 'CustomControl') {
            return React.cloneElement(child, { map });
          }
        })}
      </MapContext.Provider>
    </>
  );
};

export default memo(MapDom);
